import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/SetupIntentsService';
import {SetupIntent} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSetupIntent = (setupIntentId?: string) => {
    const queryClient = useQueryClient();

    const setupIntentQuery = useQuery({
        queryKey: ['setupIntent', setupIntentId],
        queryFn: () => fetch(setupIntentId!),
        enabled: !!setupIntentId,
    });

    // ⚠️ The payload is NO LONGER shaped here. `add` whitelists to
    // CreateSetupIntentInput itself (FNP-996), so the caller cannot leak a
    // field by forgetting a `delete` — and the live Formik values object is
    // never mutated on the way through.
    const createMutation = useMutation({
        mutationFn: (values: SetupIntent) => add(values),
        onSuccess: () => {
            notifySuccess('Setup intent created');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: SetupIntent) => {
            if (!setupIntentId) throw new Error('setupIntentId is required parameter');
            return update(setupIntentId, values);
        },
        onSuccess: () => {
            notifySuccess('Setup intent updated');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
            queryClient.invalidateQueries({queryKey: ['setupIntent', setupIntentId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!setupIntentId) throw new Error('setupIntentId is required parameter');
            return deleteRecord(setupIntentId);
        },
        onSuccess: () => {
            notifySuccess('Setup intent deleted');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
        },
        onError: notifyError,
    });

    return {
        record: setupIntentQuery.data,
        loading: setupIntentQuery.isLoading,
        fetchRecord: setupIntentQuery.refetch,
        // ⚠️ FNP-918: `mutateAsync`, NOT `mutate`.
        //
        // Every consumer of these AWAITS them — FormDrawer does
        // `await handleSubmit(values)` and then closes, and RecordForm's
        // onSubmit awaits before Formik clears `isSubmitting`. `mutate` returns
        // `void` synchronously, so awaiting it resolves on the next microtask:
        // the drawer closed while the request was still in flight, and the
        // submit button's `disabled={isSubmitting}` never engaged — leaving it
        // live for a second click. On a money-path create that is a duplicate
        // setup intent.
        //
        // `mutateAsync` rejects on failure, so the awaiting caller must handle
        // it; the drawers do, and `onError` still fires the notification.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
